import { useNormalCase } from "../composables/UseCase";
import { UseDecodeToken } from "../composables/UseDecodeToken";
import { saveStorage, getStorage, clearStorage } from "../composables/UseStorage";
import type { AuthState } from "../types/auth/authState";
import type { LoginResponse } from "../types/auth/loginResponse";
import type { TokenData } from "../types/auth/tokenData";
import type { User } from "../types/auth/user";
import type { MenuItem } from "../types/menuItem";
import { useRuntimeConfig } from "nuxt/app";
import type { CurrentFilters } from "~/types/auth/CurrentFilters";
import type { Hierarchy } from "~/types/hierarchy";

const { toNormalCase } = useNormalCase();

export const useAuthStore = defineStore("auth", {
    state: (): AuthState => ({
        authToken: null,
        isError: false,
        message: "Loading...",
        tokens: null,
        cognito_username: null,
        user: null,
        refreshOrganisationKey: 0,
    }),
    getters: {
        UseAuthorization(): Object {
            return {
                headers: {
                    Authorization: `Bearer ${this.authToken}`,
                },
            };
        },
        getUser: (state: AuthState): User | null => {
            if (!state.user) {
                const store = getStorage(useRuntimeConfig().public.storageKeys.auth) as TokenData | null;
                if (store && store.user) {
                    state.user = store.user;
                    return state.user;
                }
            }
            return state.user;
        },
        getUserFirstName(state: AuthState): string | null {
            return state.user?.FirstName ?? null;
        },
        getUserFullName(state: AuthState): string | null {
            return state.user?.FirstName + ' ' + state.user?.LastName;
        },
        getUserRole(state: AuthState): string | null {
            let role = state.user?.Roles[0]?.Name ?? null;
            if (role) {
                return toNormalCase(role);
            }
            return null;
        },
        getSelectedOrganisation: (state: AuthState): CurrentFilters | null => {
            return state.user?.CurrentFilters ?? null;
        },
        getUserPortal: (state: AuthState): string=> {
            return state.user?.PortalType;
        },
    },
    actions: {
        setAuthToken(token: string): void {
            this.authToken = token;
        },
        login(tokens: TokenData): void {
            this.authToken = tokens.id_token;
            this.tokens = tokens;
            const decodedToken = UseDecodeToken(this.tokens.id_token);
            this.cognito_username = decodedToken["cognito:username"];
            const store = { ...this.tokens, cognito_username: this.cognito_username };
            saveStorage(useRuntimeConfig().public.storageKeys.auth, store);
        },
        recordError(error: any): void {
            this.message = "Sorry, Accelerate appears to be offline. Please try again later.";
            this.isError = true;
            console.error(error);
        },
        getStorage(): void {
            const tokens = getStorage(useRuntimeConfig().public.storageKeys.auth) as TokenData | null;
            if (tokens) {
                this.tokens = tokens;
                this.authToken = tokens.id_token;
                this.user = tokens.user;
            }
        },
        setNewFilters(filters: CurrentFilters): void {
            if (!this.user) {
                return;
            }
            this.user.CurrentFilters = filters;
            const store = { ...this.tokens, user: this.user };
            saveStorage(useRuntimeConfig().public.storageKeys.auth, store);
            this.refreshOrganisationKey++;
        },
        clear(): void {
            this.tokens = null;
            this.authToken = null;
            this.user = null;
            this.cognito_username = null;
            clearStorage(useRuntimeConfig().public.storageKeys.auth);
            clearStorage(useRuntimeConfig().public.storageKeys.hierarchy);
            clearStorage(useRuntimeConfig().public.storageKeys.theme);
            //Clear out any additional nla-accelerate table keys
            const keys = Object.keys(localStorage);
            for (const key of keys) {
                if (key.startsWith("nla-accelerate-table-") || key.startsWith("nla-accelerate-")) {
                    localStorage.removeItem(key);
                }
            }
        },
        isLoginExpired(): boolean {
            this.getStorage();
            if (!this.tokens) {
                this.clear();
                return true;
            }
            const decodedToken = UseDecodeToken(this.tokens.id_token);
            if (!decodedToken) {
                this.clear();
                return true;
            }
            if (decodedToken.exp < Date.now() / 1000) {
                this.clear();
                return true;
            }
            return false;
        },
        isRefreshValid(): boolean {
            if (!this.tokens) {
                return false;
            }
            const decodedToken = UseDecodeToken(this.tokens.refresh_token);
            if (decodedToken.exp < Date.now() / 1000) {
                return false;
            }
            return true;
        },
        saveUser(user: User): void {
            this.user = user;
            const store = { user: user, ...this.tokens };
            saveStorage(useRuntimeConfig().public.storageKeys.auth, store);
        },
        async getUserDetails(): Promise<void> {
            this.message = "Retrieving profile...";
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/user/details`, {
                    method: "GET",
                    ...this.UseAuthorization,
                });
                const data = await response.json();
                if (response.ok) {
                    this.saveUser(data as User);
                } else {
                    this.recordError(data);
                }
            } catch (error) {
                this.recordError(error);
            }
        },
        async getHierarchyDetail(): Promise<void> {
            this.message = "Retrieving organisation details...";
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/user/accessible-hierarchy`, {
                    method: "GET",
                    ...this.UseAuthorization,
                });
                const data: Hierarchy[] = await response.json();
                if (response.ok) {
                    saveStorage(useRuntimeConfig().public.storageKeys.hierarchy, data, false);
                } else {
                    this.recordError("Failed to retrieve organisation details");
                }
            } catch (error) {
                this.recordError(error);
            }
        },
        async getHostedUrl(): Promise<LoginResponse | null> {
            this.message = "Checking login status...";
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/public/auth/cognito/login`);
                const data = await response.json();
                if (response.status === 200 && data.url) {
                    this.message = "Redirecting to login...";
                    return { url: data.url, external: true };
                } else {
                    this.recordError(data);
                    return null;
                }
            } catch (error) {
                this.recordError(error);
                return null;
            }
        },
        async getAccessibleMenus(): Promise<void> {
            this.message = "Preparing menu...";

            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/user/accessible-menus`, {
                    method: "GET",
                    ...useAuthStore().UseAuthorization,
                });

                if (response.ok) {
                    const data: MenuItem[] = await response.json();

                    const activeMenus = data.filter((item: MenuItem) => item.IsActive) as MenuItem[];

                    const groupedMenus = activeMenus.reduce((acc: { [key: string]: MenuItem[] }, item: MenuItem) => {
                        if (!acc[item.MenuGroup]) {
                            acc[item.MenuGroup] = [];
                        }
                        acc[item.MenuGroup].push(item);
                        return acc;
                    }, {});

                    const orderedMenus = Object.keys(groupedMenus).sort((a, b) => Number(a) - Number(b)).reduce((acc: { [key: string]: MenuItem[] }, key: string) => {
                        acc[key] = groupedMenus[key];
                        return acc;
                    }, {});

                    saveStorage(useRuntimeConfig().public.storageKeys.menu, orderedMenus, false);
                } else {
                    this.message = "Failed to prepare menu";
                }
            } catch (error) {
                this.recordError(error);
            }
        },
        async postExchangeCodeForToken(code: string): Promise<LoginResponse | null> {
            this.message = "Validating Login...";
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/public/auth/cognito/exchange`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ cognito_code: code }),
                });

                const data = await response.json();
                if (response.ok) {
                    this.login(data.tokens as TokenData);
                    await this.getUserDetails();
                    this.message = `Welcome to Accelerate ${this.user?.FirstName ? ", " + this.user.FirstName : ""}.`;
                    await this.getHierarchyDetail();
                    await this.getAccessibleMenus();
                    const url = this.user?.PortalType === 'dealer' ? '/dealer/dashboard' : '/dashboard'
                    return { url: url, external: false };
                } else {
                    this.recordError(data);
                    return null;
                }
            } catch (error) {
                this.recordError(error);
                return null;
            }
        },
        async refreshLogin(): Promise<boolean> {
            this.message = null;
            this.getStorage();
            if (!this.tokens || !this.isRefreshValid()) {
                this.message = "Session expired, please log in again.";
                return false;
            }
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/auth/cognito/exchange`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        refresh_token: this.tokens.refresh_token,
                        username: this.tokens["cognito_username"],
                    }),
                });

                const data = await response.json();
                if (response.ok) {
                    this.login(data.tokens as TokenData);
                    return true;
                } else {
                    this.clear();
                    this.message = "Session expired, please log in again.";
                    return false;
                }
            } catch (error) {
                this.recordError(error);
                return false;
            }
        },
        async signOut(): Promise<boolean> {
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/user/logout`, {
                    method: "POST",
                    ...this.UseAuthorization,
                });

                if (response.ok) {
                    this.clear();
                    return true;
                } else {
                    this.clear();
                    console.error("Error during sign out, but session was cleared.");
                    return false;
                }
            } catch (error) {
                this.recordError(error);
                this.clear();
                return false;
            }
        },
    },
});
